import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Input, Modal, Space, Spin, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import { useCalibrationRecord, useReviewCalibrationRecord } from '@/hooks/useCalibration';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_CALIBRATION_RECORD } from '@/constants/document-entity-types';
import type { CalibrationReading } from '@/services/calibration.service';
import { formatDate, formatDateTime } from '@/utils/format/date';
import { TRACEABILITY_OPTIONS, optionLabel } from '../tmmde/tmmdeDisplay';

const { Title, Text } = Typography;

const REVIEW_COLORS: Record<string, string> = { PENDING: 'gold', ACCEPTED: 'green', REJECTED: 'red' };
const RESULT_COLORS: Record<string, string> = { Pass: 'green', Fail: 'red', Conditional: 'orange', Pending: 'default' };

const CalibrationRecordDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const has = (code: string) => Boolean(user?.is_super_admin || user?.permissions?.includes(code));
  const { data: record, isLoading } = useCalibrationRecord(id || '');
  const review = useReviewCalibrationRecord();
  const [decision, setDecision] = useState<'ACCEPT' | 'REJECT' | null>(null);
  const [remarks, setRemarks] = useState('');

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!record) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message="Calibration record not found" />
      </div>
    );
  }

  const pending = (record.quality_review_status || 'PENDING') === 'PENDING';
  const canReview = has('calibration.record.review') && pending;
  const canEdit = has('calibration.record.create') && pending;
  const acceptable = record.result === 'Pass' || record.result === 'Conditional';
  const equipmentId = record.equipment_id || record.calibration_schedule?.equipment_id;
  const equipmentLabel = record.calibration_schedule
    ? [record.calibration_schedule.equipment_code, record.calibration_schedule.equipment_name].filter(Boolean).join(' — ')
    : equipmentId || '—';

  const submitReview = async () => {
    if (!decision || !id) return;
    if (decision === 'REJECT' && !remarks.trim()) return;
    await review.mutateAsync({ id, decision, remarks: remarks.trim() || undefined });
    setDecision(null);
    setRemarks('');
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(equipmentId ? `/qms/tmmde/${equipmentId}` : '/grc/calibration')}
      >
        Back
      </Button>
      <Space style={{ width: '100%', justifyContent: 'space-between', margin: '8px 0 16px' }} wrap>
        <Space direction="vertical" size={0}>
          <Title level={3} style={{ margin: 0 }}>
            Calibration {record.calibration_number}
          </Title>
          <Space>
            <Tag color={RESULT_COLORS[record.result]}>{record.result}</Tag>
            <Tag color={REVIEW_COLORS[record.quality_review_status || 'PENDING']}>
              Quality review: {record.quality_review_status || 'PENDING'}
            </Tag>
          </Space>
        </Space>
        <Space wrap>
          {canEdit && (
            <Button icon={<EditOutlined />} onClick={() => navigate(`/grc/calibration/records/${record.id}/edit`)}>
              Edit
            </Button>
          )}
          {canReview && (
            <>
              <Button danger icon={<CloseOutlined />} onClick={() => setDecision('REJECT')}>
                Reject
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={!acceptable}
                title={acceptable ? undefined : 'Only Pass or Conditional results can be accepted'}
                onClick={() => setDecision('ACCEPT')}
              >
                Accept
              </Button>
            </>
          )}
        </Space>
      </Space>

      {pending && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Awaiting Quality review"
          description="Check the certificate and readings against the acceptance criteria. Accepting rolls the due date forward and releases the equipment; rejecting puts it on hold."
        />
      )}
      {!pending && record.review_remarks && (
        <Alert
          type={record.quality_review_status === 'ACCEPTED' ? 'success' : 'error'}
          showIcon
          style={{ marginBottom: 16 }}
          message={`Review remarks (${formatDateTime(record.reviewed_at)})`}
          description={record.review_remarks}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, md: 2, xl: 3 }}>
          <Descriptions.Item label="Equipment">
            {equipmentId ? (
              <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/tmmde/${equipmentId}`)}>
                {equipmentLabel}
              </Button>
            ) : (
              equipmentLabel
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Schedule">{record.calibration_schedule?.calibration_code || '—'}</Descriptions.Item>
          <Descriptions.Item label="Calibration date">{formatDate(record.calibration_date, { nullDisplay: '—' })}</Descriptions.Item>
          <Descriptions.Item label="Valid until">{formatDate(record.calibration_valid_until, { nullDisplay: '—' })}</Descriptions.Item>
          <Descriptions.Item label="Next due">{formatDate(record.next_calibration_due_date, { nullDisplay: '—' })}</Descriptions.Item>
          <Descriptions.Item label="Certificate no.">{record.calibration_certificate_number || '—'}</Descriptions.Item>
          <Descriptions.Item label="Supplier / provider">
            {record.supplier_name || record.calibration_provider || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Calibrated by">{record.calibrator_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Adjusted">{Number(record.adjusted) ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Traceability">{optionLabel(TRACEABILITY_OPTIONS, record.traceability_basis)}</Descriptions.Item>
          <Descriptions.Item label="Uncertainty">{record.measurement_uncertainty || '—'}</Descriptions.Item>
          <Descriptions.Item label="Environment">{record.environmental_conditions || '—'}</Descriptions.Item>
          <Descriptions.Item label="As found">{record.as_found_reading || '—'}</Descriptions.Item>
          <Descriptions.Item label="As left">{record.as_left_reading || '—'}</Descriptions.Item>
          <Descriptions.Item label="Standard">{record.calibration_standard || '—'}</Descriptions.Item>
          {record.result === 'Fail' && (
            <>
              <Descriptions.Item label="Failure reason" span={3}>
                {record.failure_reason || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Corrective action" span={3}>
                {record.corrective_action || '—'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Notes" span={3}>
            {record.calibration_notes || '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Readings" style={{ marginBottom: 16 }}>
        <Table<CalibrationReading>
          rowKey={(r, i) => r.id || String(i)}
          size="small"
          pagination={false}
          dataSource={record.readings || []}
          locale={{ emptyText: 'No test-point readings recorded' }}
          columns={[
            { title: 'Test point', dataIndex: 'test_point', render: (v) => v || '—' },
            { title: 'Nominal', dataIndex: 'nominal_value', render: (v) => v || '—' },
            { title: 'Tolerance', dataIndex: 'tolerance', render: (v) => v || '—' },
            { title: 'As found', dataIndex: 'as_found', render: (v) => v || '—' },
            { title: 'As left', dataIndex: 'as_left', render: (v) => v || '—' },
            { title: 'Unit', dataIndex: 'unit', render: (v) => v || '—' },
            {
              title: 'Result',
              dataIndex: 'result',
              render: (v) => (v ? <Tag color={v === 'PASS' ? 'green' : 'red'}>{v}</Tag> : '—'),
            },
          ]}
        />
      </Card>

      <EntityAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_CALIBRATION_RECORD}
        entityId={record.id}
        title="Calibration certificate and evidence"
        readOnly={!has('calibration.record.create')}
        queryKeyPrefix={['documents', 'calibration-record']}
        uploadHint="Upload the calibration certificate (PDF or image)."
      />

      <Modal
        open={Boolean(decision)}
        title={decision === 'ACCEPT' ? 'Accept calibration' : 'Reject calibration'}
        okText={decision === 'ACCEPT' ? 'Accept' : 'Reject'}
        okButtonProps={{
          danger: decision === 'REJECT',
          disabled: decision === 'REJECT' && !remarks.trim(),
          loading: review.isPending,
        }}
        onOk={() => void submitReview()}
        onCancel={() => {
          setDecision(null);
          setRemarks('');
        }}
        destroyOnHidden
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {decision === 'ACCEPT'
            ? 'The schedule due date rolls forward and the equipment is released for use.'
            : 'The equipment will be marked Not for use.'}
        </Text>
        <Input.TextArea
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={decision === 'REJECT' ? 'Reason (required)' : 'Remarks (optional)'}
        />
      </Modal>
    </div>
  );
};

export default CalibrationRecordDetailPage;
